import React, { useCallback, useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { useParams } from 'react-router-dom';
import {
  getPublicStockItem,
  openPublicStockItem,
} from '../../clients/stock_item_scan_client';
import { formatDateStr } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';
import './StockItemOpenPage.css';

const LOG_CONTEXT = 'StockItemOpenPage';

/**
 * Public confirmation page at /inventory/open/:token — no login required.
 * Loads stock item details and allows marking the item as opened.
 */
const StockItemOpenPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [openedByName, setOpenedByName] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSubmitMessage('');
    const result = await getPublicStockItem(token);
    setLoading(false);
    if (!result.success) {
      const message =
        result.status === 404
          ? 'This scan link is invalid or has expired.'
          : result.messages?.[0] || 'Unable to load this stock item.';
      setError(message);
      setData(null);
      return;
    }
    setData(result.data);
    logInfo(LOG_CONTEXT, 'stock item loaded', {
      stockItemId: result.data?.stock_item_id,
      alreadyOpen: result.data?.already_open,
    });
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkOpened = async () => {
    setBusy(true);
    setSubmitMessage('');
    const trimmedName = openedByName.trim();
    const result = await openPublicStockItem(token, {
      opened_by_name: trimmedName || undefined,
    });
    setBusy(false);
    if (!result.success) {
      setSubmitMessage(result.messages?.[0] || 'Could not mark item as opened.');
      return;
    }
    setData(result.data);
    if (result.data.opened_just_now) {
      setSubmitMessage('Item marked as opened.');
      logInfo(LOG_CONTEXT, 'item opened', {
        stockItemId: result.data.stock_item_id,
      });
    } else if (result.data.already_open) {
      setSubmitMessage('This item was already opened.');
    }
  };

  if (loading) {
    return (
      <div className="stock-item-open-page">
        <Container className="py-5 text-center">
          <Spinner animation="border" role="status" />
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stock-item-open-page">
        <Container className="py-4">
          <Card className="stock-item-open-card shadow-sm">
            <Card.Body className="text-center py-5">
              <Alert variant="danger" className="mb-0">
                {error}
              </Alert>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  const alreadyOpen = Boolean(data?.already_open);

  return (
    <div className="stock-item-open-page">
      <Container className="py-4">
        <Card className="stock-item-open-card shadow-sm">
          <Card.Body className="p-4">
            <h1 className="stock-item-open-title h4 mb-1">{data.product_name}</h1>
            <p className="text-muted mb-4">Catalog #{data.cat_num}</p>

            <dl className="stock-item-open-details mb-4">
              <div className="stock-item-open-details__row">
                <dt>Batch</dt>
                <dd>{data.batch || '—'}</dd>
              </div>
              <div className="stock-item-open-details__row">
                <dt>Expiry</dt>
                <dd>{data.expiry ? formatDateStr(data.expiry) : '—'}</dd>
              </div>
              {alreadyOpen ? (
                <>
                  <div className="stock-item-open-details__row">
                    <dt>Opened on</dt>
                    <dd>
                      {data.opened_on ? formatDateStr(data.opened_on) : '—'}
                    </dd>
                  </div>
                  {data.opened_by_name ? (
                    <div className="stock-item-open-details__row">
                      <dt>Opened by</dt>
                      <dd>{data.opened_by_name}</dd>
                    </div>
                  ) : null}
                </>
              ) : null}
            </dl>

            {submitMessage ? (
              <Alert
                variant={data.opened_just_now ? 'success' : 'info'}
                className="mb-3"
              >
                {submitMessage}
              </Alert>
            ) : null}

            {alreadyOpen ? (
              <Alert variant="secondary" className="mb-0 text-center">
                This item has already been marked as opened.
              </Alert>
            ) : (
              <>
                <Form.Group className="mb-4">
                  <Form.Label htmlFor="opened-by-name">Your name (optional)</Form.Label>
                  <Form.Control
                    id="opened-by-name"
                    type="text"
                    value={openedByName}
                    onChange={e => setOpenedByName(e.target.value)}
                    placeholder="Jane Doe"
                    disabled={busy}
                  />
                </Form.Group>
                <Button
                  variant="primary"
                  size="lg"
                  className="stock-item-open-action w-100"
                  disabled={busy}
                  onClick={handleMarkOpened}
                >
                  {busy ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                        role="status"
                      />
                      Saving…
                    </>
                  ) : (
                    'Mark as opened'
                  )}
                </Button>
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default StockItemOpenPage;
